import { type Reference, useApolloClient } from '@apollo/client'
import { Dialog } from '@factorialco/f0-react/dist/experimental'
import { EngagementArchiveSurveyDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  surveyId: string
  onCancel?: () => void
  onSuccess?: () => void
}

export function ArchiveSurveyDialog({ surveyId, onCancel, onSuccess }: Props) {
  const apolloClient = useApolloClient()
  const [archiveSurvey] = useMutation(EngagementArchiveSurveyDocument, {
    extractorFn: (data) => data.surveysMutations.archiveSurveysSurvey,
  })

  return (
    <Dialog
      open
      onClose={onCancel}
      header={{
        title: i18n.t('surveys.archive_survey_dialog.title'),
        description: i18n.t('surveys.archive_survey_dialog.description'),
        type: 'critical',
      }}
      actions={{
        primary: {
          label: i18n.t('common.archive'),
          variant: 'critical',
          onClick: async () => {
            const response = await archiveSurvey({
              variables: {
                surveyId: surveyId.toString(),
              },
            })

            const handled = handleResult(response, () => {})

            if (handled.success) {
              const normalizedId = apolloClient.cache.identify({
                id: surveyId,
                __typename: 'SurveysSurvey',
              })

              apolloClient.cache.modify({
                id: 'ROOT_QUERY',
                fields: {
                  surveys(existing, { readField }) {
                    const connection = readField<{
                      edges: readonly unknown[]
                      totalCount: number
                    }>('surveysConnection', existing)

                    if (!connection?.edges) return existing

                    const filteredEdges = connection.edges.filter((edge) => {
                      const node = readField<Reference>('node', edge as Reference)
                      return node?.__ref !== normalizedId
                    })

                    return {
                      ...existing,
                      surveysConnection: {
                        ...connection,
                        edges: filteredEdges,
                        totalCount: (connection.totalCount ?? 1) - 1,
                      },
                    }
                  },
                },
              })

              if (normalizedId) {
                apolloClient.cache.evict({ id: normalizedId })
                apolloClient.cache.gc()
              }

              onSuccess?.()
            }
          },
        },
        secondary: {
          label: i18n.t('common.cancel'),
          onClick: () => onCancel?.(),
        },
      }}
    />
  )
}
